import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  MenuItem,
  Button,
  Typography,
  TextField,
  FormControl,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  useTheme,
} from "@mui/material";
import { END_POINT } from "../../utils/End-points";
import ApiClient from "../../apis/Api-client";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
  selectTriggered,
  setTriggered,
} from "../../core/store/redux/slices/DashboardApi-slice";
import { errorToster, successToster } from "../../shared/toster/Toster";
import { Cancel, Edit } from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";
import { Helper } from "../../utils/Helpers";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  CLEANING_STATUSES,
  OCCUPANCY_STATUSES,
  RESERVATION_STATUSES,
} from "../../utils/Constants";
import { format } from "date-fns";
import { Trans, useTranslation } from "react-i18next";
import { setDataToDB } from "../../db/dbHelpers";
import EditRent from "../editRent/EditRent";
import IconConstants from "../../utils/IconConstants";
import ConfirmationDlgBox from "../common-components/confirmation-popup";
import { Can, FEATURES, ACTIONS } from "../../core/permissions";
const { TENTATIVE, CONFIRMED } = RESERVATION_STATUSES;
const { CLEAN, DIRTY, ASSIGNED } = CLEANING_STATUSES;
const CLEANING_STATUS_ICONS = {
  [CLEAN]: IconConstants.CleanRoomsIcon,
  [ASSIGNED]: IconConstants.CleaningPurple,
  [DIRTY]: IconConstants.UnCleanRoomsIcon,
};
const CLEANING_STATUS_LABELS = {
  [DIRTY]: "Dirty",
  [ASSIGNED]: "Assigned",
};
const WARN_ON_CLEANING_STATUSES = [DIRTY, ASSIGNED];
const {
  getLocalTimeFromUtc,
  getBasePriceFromFomNetRent,
  getNetRetFromBasePrice,
  debounce,
  fetchAndFilterVacantRooms,
  getGradientTextStyle,
  applyGraceLabelsToDayRents,
  consolidateTaxRatesSlabs,
  getTaxPercentForAmount,
  getTaxBreakdown,
  filterRatePlansForRoomType,
  getStayCalculation,
} = new Helper();

/**
 * Normalizes HMS UI date labels and raw date strings into standard YYYY-MM-DD.
 * e.g., "15-04-26(1hr)" -> "2026-04-15"
 * e.g., "2026-04-15" -> "2026-04-15"
 */
const normalizeRoomShiftDate = (dateStr) => {
  if (!dateStr) return "";

  let rawDate = dateStr.split("(")[0].trim();
  if (rawDate.endsWith("-")) {
    rawDate = rawDate.slice(0, -1);
  }

  // 2. If already in YYYY-MM-DD format (with or without time)
  if (/^\d{4}-\d{2}-\d{2}/.test(rawDate)) {
    return rawDate.split(" ")[0]; // Extract only the date part
  }

  // 3. Handle DD-MM-YY or DD-MM-YYYY
  const parts = rawDate.split("-");
  if (parts.length === 3) {
    let day = parts[0].padStart(2, '0');
    let month = parts[1].padStart(2, '0');
    let year = parts[2];

    // Handle 2-digit years
    if (year.length === 2) {
      const centuryPrefix = new Date().getFullYear().toString().substring(0, 2);
      year = `${centuryPrefix}${year}`;
    }

    // Return in standard YYYY-MM-DD
    if (year.length === 4) {
      return `${year}-${month}-${day}`;
    }
  }

  return rawDate;
};

/**
 * Extracts grace hours from a date label string like "08-05-26(5hr)"
 */
const extractGraceHoursFromLabel = (dateStr) => {
  const match = dateStr?.match(/\((\d+)hr\)/);
  return match ? Number(match[1]) : null;
};

const RoomShift = ({
  bookingData,
  resetData = () => { },
  handleCloseModal = () => { },
  handleClosePopover = () => { },
}) => {
  const { t } = useTranslation();
  const { api } = ApiClient();
  const { palette } = useTheme();
  const isTriggered = useSelector(selectTriggered);
  const dispatch = useDispatch();
  const [availableRoomsByType, setAvailableRoomsByType] = useState([]);
  const [reservationStatuses, setReservationStatuses] = useState([]);
  const [roomShiftReasons, setRoomShiftReasons] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [ratePlans, setRatePlans] = useState([]);
  const [apiPricingData, setApiPricingData] = useState(null); // To store raw API breakdown
  const fetchedConfig = useRef(null); // To store the config used for the last API fetch
  const [roomRentDisable, setRoomRentDisable] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [taxRates, setTaxRates] = useState([]);
  const [stayTypes, setStayTypes] = useState([]);
  const [dirtyRoomDialog, setDirtyRoomDialog] = useState({
    open: false,
    roomNo: null,
    statusLabel: null,
  });
  const isOccupancyRoomShift =
    bookingData?.booking_status === OCCUPANCY_STATUSES.CHECK_IN;
  const isCmBooking = !!(bookingData?.channel_manager_booking_id || bookingData?.data?.channel_manager_booking_id);

  // Smart Pricing Refs
  const originalConfig = useRef(null);
  const originalChildPrice = useRef(0);
  const pricingSessionDate = useRef(null);

  // Helper to update rate types based on children presence
  const getUpdatedRateType = (currentRateType, newChildren) => {

    switch (currentRateType) {
      case "default":
        return newChildren > 0 ? "defaultwithchildren" : "default";
      case "specific":
        return newChildren > 0 ? "specificwithchildren" : "specific";
      case "defaultwithchildren":
        return newChildren === 0 || !newChildren ? "default" : "defaultwithchildren";
      case "specificwithchildren":
        return newChildren === 0 || !newChildren ? "specific" : "specificwithchildren";
      case "edited":
        return "edited";
      default:
        return currentRateType;
    }
  };

  const formConfig = () => {
    const getRoomType = (room_type_id) =>
      roomTypes.find((v) => v.room_type_id === room_type_id);

    return Yup.object().shape({
      room_type_id: Yup.number()
        .transform((value) => (Number.isNaN(value) ? null : value))
        .required("Room type is required"),
      room_id: Yup.number()
        .transform((value) => (Number.isNaN(value) ? null : value))
        .required(t("pages.booking_form.alert.room_number_required")),
      rate_plan_id: Yup.number()
        .transform((value) => (Number.isNaN(value) ? null : value))
        .required("Rate plan is required"),
      no_of_adults: Yup.number()
        .transform((value) => (Number.isNaN(value) ? null : value))
        .required("Number of adults is required")
        .min(1, "Minimum of 1 adult should be selected")
        .test(
          "max-adults",
          "Number of adults exceeds the limit",
          function (value) {
            const { room_type_id } = this.parent;
            const roomType = getRoomType(room_type_id);
            if (roomType && value > roomType.max_adult) {
              return this.createError({
                message: t(
                  `pages.booking_form.alert.only_adults_are_permitted`,
                  {
                    count: roomType.max_adult,
                  }
                ),
              });
            }
            return true;
          }
        ),
      no_of_children: Yup.number()
        .transform((value) => (Number.isNaN(value) ? null : value))
        .notRequired()
        .test(
          "max-children",
          "Number of children exceeds the limit",
          function (value) {
            const { room_type_id } = this.parent;
            const roomType = getRoomType(room_type_id);
            if (roomType && value > roomType.max_children) {
              return this.createError({
                message: t(
                  `pages.booking_form.alert.only_childs_are_permitted`,
                  {
                    count: roomType.max_children,
                  }
                ),
              });
            }
            return true;
          }
        ),
      room_rent: Yup.string()
        .required("Please enter room rent")
        .test("is-number-or-message", "Please enter a valid rent", (value) => {
          if (value === "Multiple Payments Applied") return true;
           if (Number(value) === 0) return true; 
          return !isNaN(Number(value)) && Number(value) >= 1;
        }),
      room_shift_reason_id: Yup.number()
        .transform((value) => (Number.isNaN(value) ? null : value))
        .nullable()
        .test(
          "is-required-if-occupancy",
          t("common_components.room_shift_popup.alert.please_select_reason"),
          function (value) {
            return isOccupancyRoomShift ? !!value : true;
          }
        ),
    });
  };

  const localCheckin = getLocalTimeFromUtc(bookingData?.checkin_date)?.dateTimeString;
  const localCheckout = getLocalTimeFromUtc(bookingData?.checkout_date || bookingData?.expected_checkout_date)?.dateTimeString;
  const matchedRoom = bookingData?.room_data?.[0]?.booked_rooms?.find(room =>
    (bookingData?.occupancy_id && room.occupancy_id === bookingData.occupancy_id) ||
    (bookingData?.room_id && room.room_id === bookingData.room_id)
  );
  const initialRatePlanId =
    bookingData?.rate_plan_id ||
    matchedRoom?.rate_plan_id ||
    bookingData?.room_data?.[0]?.rate_plan_id ||
    "";

  const stayTypeCode = bookingData?.stay_type_code || matchedRoom?.stay_type_code || bookingData?.room_data?.[0]?.stay_type_code;
  const stay_type_id = bookingData?.stay_type_id || matchedRoom?.stay_type_id || bookingData?.room_data?.[0]?.stay_type_id;
  const stayTypeObj = stayTypes.find((st) => st.stay_type_id === stay_type_id || st.stay_type_code === stayTypeCode);

  const todayDateStr = format(new Date(), "yyyy-MM-dd");
  const boundaryDateStr = (() => {
    if (!isOccupancyRoomShift) return null;
    const currentDate = new Date();
    const type = stayTypeCode?.toLowerCase();
    if (type === "noon") {
      const todayStr = format(currentDate, "yyyy-MM-dd");
      const noonLocal = new Date(`${todayStr}T12:00:00`);
      if (currentDate < noonLocal) {
        const prevDay = new Date(currentDate);
        prevDay.setDate(prevDay.getDate() - 1);
        return format(prevDay, "yyyy-MM-dd");
      }
      return todayStr;
    } else if (type === "standard") {
      const stdCheckoutTime = stayTypeObj?.standard_checkout_time || "12:00:00";
      const todayStr = format(currentDate, "yyyy-MM-dd");
      const checkoutLocal = new Date(`${todayStr}T${stdCheckoutTime}`);
      if (currentDate < checkoutLocal) {
        const prevDay = new Date(currentDate);
        prevDay.setDate(prevDay.getDate() - 1);
        return format(prevDay, "yyyy-MM-dd");
      }
      return todayStr;
    } else if (type === "day" && localCheckin) {
      const checkinTime = new Date(localCheckin);
      const todayStr = format(currentDate, "yyyy-MM-dd");
      const checkinCompare = new Date(
        `${todayStr}T${format(checkinTime, "HH:mm:ss")}`
      );
      if (currentDate < checkinCompare) {
        const prevDay = new Date(currentDate);
        prevDay.setDate(prevDay.getDate() - 1);
        return format(prevDay, "yyyy-MM-dd");
      }
      return todayStr;
    }
    return format(currentDate, "yyyy-MM-dd");
  })();

  const newRoomCheckin = (() => {
    if (!isOccupancyRoomShift || !boundaryDateStr) return localCheckin;
    const type = stayTypeCode?.toLowerCase();
    if (type === "noon") {
      return `${boundaryDateStr}T12:00:00`;
    } else if (type === "standard") {
      const stdCheckinTime = stayTypeObj?.standard_checkin_time || "12:00:00";
      return `${boundaryDateStr}T${stdCheckinTime}`;
    } else if (type === "day" && localCheckin) {
      const timePart = localCheckin.split(" ")[1] || "12:00:00";
      return `${boundaryDateStr}T${timePart}`;
    }
    return `${boundaryDateStr}T12:00:00`;
  })();

  const checkinDateStr = bookingData?.checkin_date ? format(new Date(bookingData.checkin_date), "yyyy-MM-dd") : null;
  const isNextDayShift = isOccupancyRoomShift && checkinDateStr && (todayDateStr !== checkinDateStr);

  const rawDayRents =
    bookingData?.day_rents ||
    bookingData?.room_data?.[0]?.day_rents || // Path for Reservation
    matchedRoom?.day_rents || []

  // 1. Filter and Convert Dates to Local Time + Amounts to Net
  const convertedRents = rawDayRents
    .filter((rent) => {
      // If it's occupancy room shift, show only from boundaryDateStr onwards
      if (isOccupancyRoomShift && boundaryDateStr) {
        const rentDateStr = getLocalTimeFromUtc(rent.date)?.dateString;
        if (isNextDayShift && rentDateStr === checkinDateStr) {
          // Exclude early check-in grace rents on check-in date during next-day shifts
          if (rent.grace_hours || rent.isGrace) {
            return false;
          }
        }
        return rentDateStr >= boundaryDateStr;
      }
      // If it's reservation room shift, show full stay periods
      return true;
    })
    .map((rent) => {
      const netAmount = bookingData?.inc_of_tax
        ? getNetRetFromBasePrice(rent.amount, 1, rent.tax_rate)
        : rent.amount;
      return {
        ...rent,
        date: getLocalTimeFromUtc(rent.date)?.dateTimeString,
        amount: netAmount,
        original_base: netAmount,
        original_rate_type: rent.rate_type, // Adding original_rate_type here
      };
    });

  // 2. Apply Grace Labels (Detect Late Checkout rows)
  // Use localCheckin (original booking checkin) instead of newRoomCheckin (boundary-based noon time)
  // so same-day occupancy room shift correctly shows early checkin grace label (e.g. 3hr) + late checkout (10hr)
  // instead of two duplicate (10hr) rows.
  const dayrentsvalue = applyGraceLabelsToDayRents(
    convertedRents,
    localCheckin,
    localCheckout,
    stayTypeCode,
    stayTypeObj?.standard_checkin_time,
    stayTypeObj?.standard_checkout_time
  );

  const getRoomRentDisplayValue = (rents) => {
    if (rents.length === 0) return 0;
    const amounts = rents.map((r) => Number(r.amount));
    const firstAmount = amounts[0];
    const isAllSame = amounts.every((a) => a === firstAmount);
    return isAllSame ? firstAmount : "Multiple Payments Applied";
  };

  // Initialize original config ref once
  if (!originalConfig.current && bookingData) {
    originalConfig.current = {
      room_type_id: bookingData?.room_type_id,
      rate_plan_id: initialRatePlanId,
      no_of_adults: bookingData?.no_of_adults,
      no_of_children: bookingData?.no_of_children,
      day_rents: dayrentsvalue,
      room_rent: getRoomRentDisplayValue(dayrentsvalue),
    };
  }

  // Extract original child price from rate plans
  useEffect(() => {
    if (ratePlans.length > 0 && initialRatePlanId) {
      const originalRp = ratePlans.find(
        (rp) => Number(rp.rate_plan_id) === Number(initialRatePlanId)
      );
      // As per logs, child_price is at the top level of the rate plan object
      originalChildPrice.current = Number(originalRp?.child_price) || 0;
    }
  }, [ratePlans, initialRatePlanId]);

  const methods = useForm({
    resolver: yupResolver(formConfig()),
    defaultValues: {
      room_type_id: bookingData?.room_type_id || bookingData?.room_data?.[0]?.room_type_id,
      rate_plan_id: initialRatePlanId,
      room_no: bookingData?.room_no,
      inc_of_tax: bookingData?.inc_of_tax,
      no_of_adults: bookingData?.no_of_adults,
      no_of_children: bookingData?.no_of_children,
      day_rents: dayrentsvalue,
      room_rent: getRoomRentDisplayValue(dayrentsvalue),
    },
  });

  const {
    control,
    watch,
    reset,
    setValue,
    getValues,
    setError,
    clearErrors,
    handleSubmit,
  } = methods;

  const fetchPricingFromAPI = async (payload) => {
    const {
      room_type_id,
      rate_plan_id,
      no_of_adults,
      no_of_children,
    } = payload;

    // Trigger check
    if (!room_type_id || !rate_plan_id || !no_of_adults) return;

    try {
      // Dynamic Checkin: Use "Now" for occupancy shifts, or original checkin for reservations
      const apiCheckin = isOccupancyRoomShift
        ? format(new Date(), "yyyy-MM-dd HH:mm:ss")
        : localCheckin;


      const response = await api.post(END_POINT.RATE_PLAN.GET_ROOM_DAYS_PRICE, {
        checkin_date: apiCheckin,
        checkout_date: localCheckout,
        room_type_id,
        rate_plan_id,
        no_of_adults,
      });

      if (response?.status === 200) {
        const { breakdown, child_price } = response?.data?.data || {};

        // Store raw API pricing data and the config used
        setApiPricingData({ breakdown, child_price });
        fetchedConfig.current = { room_type_id, rate_plan_id, no_of_adults };

        const totalChildChargePerDay = (child_price || 0) * (no_of_children || 0);

        const newRents = (breakdown || []).map((item, idx) => {
          // Rule: API returns 'price', booking data uses 'amount'
          const netAdultOnly = Number(item.price) || 0;
          const netWithChildren = netAdultOnly + totalChildChargePerDay;

          const baseRateType = item.rate_type;
          const childRateType = `${baseRateType}withchildren`;
          const currentRateType = Number(no_of_children) > 0 ? childRateType : baseRateType;

          return {
            date: item.date,
            amount: netWithChildren,
            rate_type: currentRateType,
            // 4 Keys for Smart Revert (API Source - Direct values without tax calculation)
            api_price: netAdultOnly,
            api_ratetype: baseRateType,
            api_with_child_price: netWithChildren,
            api_with_child_ratetype: childRateType,
          };
        });
        // Smart early check-in logic
        const todayDateStr = format(new Date(), "yyyy-MM-dd");
        const checkinDateObj = new Date(localCheckin);
        const checkinDateStr = format(checkinDateObj, "yyyy-MM-dd");

        const stayTypeObj = stayTypes.find((st) => st.stay_type_id === stay_type_id || st.stay_type_code === stayTypeCode);
        const staydata = getStayCalculation(
          localCheckin,
          localCheckout,
          stayTypeCode,
          stayTypeObj?.standard_checkin_time,
          stayTypeObj?.standard_checkout_time
        );
        const isBeforeBoundary = staydata?.earlyCheckinGrace;

        const shouldApplyEarlyCheckin = isOccupancyRoomShift
          ? (todayDateStr === checkinDateStr)
          : true;

        if (shouldApplyEarlyCheckin && (stayTypeCode === "noon" || stayTypeCode === "standard") && isBeforeBoundary && newRents.length > 0) {
          const apiFirstRow = newRents[0];
          const netAdultOnly = Number(apiFirstRow.api_price) || 0;
          const netWithChildren = netAdultOnly + totalChildChargePerDay;
          const currentRateType = Number(no_of_children) > 0 ? `${apiFirstRow.api_ratetype}withchildren` : apiFirstRow.api_ratetype;

          const earlyCheckinRow = {
            date: apiFirstRow.date,
            amount: netWithChildren,
            rate_type: currentRateType,
            api_price: netAdultOnly,
            api_ratetype: apiFirstRow.rate_type,
            api_with_child_price: netWithChildren,
            api_with_child_ratetype: `${apiFirstRow.rate_type}withchildren`,
          };
          newRents.unshift(earlyCheckinRow);
        }

        // Apply Grace Labels (Late checkout etc.)
        const finalizedRents = applyGraceLabelsToDayRents(
          newRents,
          newRoomCheckin,
          localCheckout,
          stayTypeCode,
          stayTypeObj?.standard_checkin_time,
          stayTypeObj?.standard_checkout_time
        );

        setValue("day_rents", finalizedRents);
        setValue("room_rent", getRoomRentDisplayValue(finalizedRents));
      }
    } catch (error) {
      console.error("DEBUG [fetchPricingFromAPI] Error:", error);
    }
  };

  const handleSmartPricingLogic = (nextPayload) => {
    const {
      room_type_id: nextRoomId,
      rate_plan_id: nextRateId,
      no_of_adults: nextAdults,
      no_of_children: nextChildren,
    } = nextPayload;


    const original = originalConfig.current;
    if (!original) return;

    // Approach 1: Full Revert (All 4 match original)
    const isFullMatch =
      Number(nextRoomId) === Number(original.room_type_id) &&
      Number(nextRateId) === Number(original.rate_plan_id) &&
      Number(nextAdults) === Number(original.no_of_adults) &&
      Number(nextChildren) === Number(original.no_of_children);

    if (isFullMatch) {
      // No log needed or keep it simple
      reset(
        {
          ...getValues(),
          room_type_id: original.room_type_id,
          rate_plan_id: original.rate_plan_id,
          no_of_adults: original.no_of_adults,
          no_of_children: original.no_of_children,
          day_rents: original.day_rents,
          room_rent: original.room_rent,
        },
        { keepErrors: true }
      );
      return true;
    }

    // Approach 2: Smart Child Adjustment (Room, Rate, Adult match original)
    const isBaseMatch =
      Number(nextRoomId) === Number(original.room_type_id) &&
      Number(nextRateId) === Number(original.rate_plan_id) &&
      Number(nextAdults) === Number(original.no_of_adults);

    if (isBaseMatch) {
      // No log needed
      const deltaChildren =
        Number(nextChildren) - Number(original.no_of_children);
      const adjustment = deltaChildren * originalChildPrice.current;

      const currentFormRents = getValues("day_rents") || [];

      const updatedRents = original.day_rents.map((origItem) => {
        const existingRow = currentFormRents.find((r) => r.date === origItem.date);
        if (existingRow && existingRow.rate_type === "edited") {
          return existingRow;
        }

        const baseAmount = Number(origItem.amount);
        const newAmount = baseAmount + adjustment;
        const newRateType = getUpdatedRateType(
          origItem.rate_type,
          Number(nextChildren)
        );

        return {
          ...origItem,
          amount: newAmount,
          rate_type: newRateType,
          original_base: newAmount, // Update baseline for manual revert
          original_rate_type: newRateType, // Update baseline for manual revert
        };
      });

      setValue("day_rents", updatedRents);
      setValue("room_rent", getRoomRentDisplayValue(updatedRents));
      return true;
    }

    // Approach 2.5: User already fetched from API, but now only changing CHILDREN
    if (apiPricingData && fetchedConfig.current) {
      const isCurrentApiMatch =
        Number(nextRoomId) === Number(fetchedConfig.current.room_type_id) &&
        Number(nextRateId) === Number(fetchedConfig.current.rate_plan_id) &&
        Number(nextAdults) === Number(fetchedConfig.current.no_of_adults);

      if (isCurrentApiMatch) {
        const { breakdown, child_price } = apiPricingData;
        const totalChildChargePerDay = (child_price || 0) * (nextChildren || 0);

        const currentFormRents = getValues("day_rents") || [];


        const updatedRents = (breakdown || []).map((item) => {
          // 2. Check if this date has "edited" rate_type in the current form
          // We look for a row that matches the date and is NOT a grace row
          const existingRow = currentFormRents.find((r) => {
            const rNorm = normalizeRoomShiftDate(r.date);
            //  const isGraceR = r.date.includes("(");
            return rNorm === item.date
            //&& !isGrace;
          });

          if (existingRow && existingRow.rate_type === "edited") {
            return existingRow;
          }

          const netAdultOnly = Number(item.price) || 0;
          const netWithChildren = netAdultOnly + totalChildChargePerDay;

          const baseRateType = item.rate_type;
          const childRateType = `${baseRateType}withchildren`;
          const currentRateType = Number(nextChildren) > 0 ? childRateType : baseRateType;

          return {
            date: item.date,
            amount: netWithChildren,
            rate_type: currentRateType,
            api_price: netAdultOnly,
            api_ratetype: baseRateType,
            api_with_child_price: netWithChildren,
            api_with_child_ratetype: childRateType,
          };
        });

        // Smart early check-in logic for Scenario 2.5
        const todayDateStr = format(new Date(), "yyyy-MM-dd");
        const checkinDateObj = new Date(localCheckin);
        const checkinDateStr = format(checkinDateObj, "yyyy-MM-dd");

        const stayTypeObj = stayTypes.find((st) => st.stay_type_id === stay_type_id || st.stay_type_code === stayTypeCode);
        const staydata = getStayCalculation(
          localCheckin,
          localCheckout,
          stayTypeCode,
          stayTypeObj?.standard_checkin_time,
          stayTypeObj?.standard_checkout_time
        );
        const isBeforeBoundary = staydata?.earlyCheckinGrace;

        const shouldApplyEarlyCheckin = isOccupancyRoomShift
          ? (todayDateStr === checkinDateStr)
          : true;

        if (shouldApplyEarlyCheckin && (stayTypeCode === "noon" || stayTypeCode === "standard") && isBeforeBoundary && updatedRents.length > 0) {
          const existingEarlyRow = currentFormRents.find((r) => {
            const rNorm = normalizeRoomShiftDate(r.date);
            const isGraceR = r.date.includes("(");
            return rNorm === checkinDateStr && isGraceR;
          });

          if (existingEarlyRow && existingEarlyRow.rate_type === "edited") {
            updatedRents.unshift(existingEarlyRow);
          } else {
            // SAFE DUPLICATION: Don't copy from potentially edited updatedRents[0]
            const apiFirstRow = breakdown[0];
            const netAdultOnly = Number(apiFirstRow.price) || 0;
            const netWithChildren = netAdultOnly + totalChildChargePerDay;
            const currentRateType = Number(nextChildren) > 0 ? `${apiFirstRow.rate_type}withchildren` : apiFirstRow.rate_type;

            const earlyCheckinRow = {
              date: apiFirstRow.date,
              amount: netWithChildren,
              rate_type: currentRateType,
              api_price: netAdultOnly,
              api_ratetype: apiFirstRow.rate_type,
              api_with_child_price: netWithChildren,
              api_with_child_ratetype: `${apiFirstRow.rate_type}withchildren`,
            };
            updatedRents.unshift(earlyCheckinRow);
          }
        }

        const finalizedRents = applyGraceLabelsToDayRents(
          updatedRents,
          newRoomCheckin,
          localCheckout,
          stayTypeCode,
          stayTypeObj?.standard_checkin_time,
          stayTypeObj?.standard_checkout_time
        );

        setValue("day_rents", finalizedRents);
        setValue("room_rent", getRoomRentDisplayValue(finalizedRents));
        return true;
      }
    }

    // Scenario 3: Call API for New Configuration
    fetchPricingFromAPI(nextPayload);

    return true;
  };

  const {
    name,
    checkin_date,
    checkout_date,
    inc_of_tax,
    occupancy_key,
    reservation_id,
  } = bookingData;
  const {
    room_type_id,
    room_id,
    no_of_adults,
    no_of_children,
    room_shift_reason_id,
    rate_plan_id,
  } = watch();

  useEffect(() => {
    setInitialData();
  }, []);

  const setInitialData = async () => {
    try {
      const [roomTypes, reservationStatus, roomShiftReasons, taxRates, ratePlansRes, stayTypesRes] =
        await Promise.all([
          api
            .get(END_POINT.ROOM_TYPE.GET_ALL_ROOM_TYPE)
            .then((res) => res?.data?.data),
          api
            .get(END_POINT.RESERVATION.GET_ALL_RESERVATION_STATUS)
            .then((res) => res?.data?.data),
          api
            .get(END_POINT.ROOM.GET_ALL_ROOM_SHIFT_REASON)
            .then((res) => res?.data?.data),
          api
            .get(END_POINT.TAX.GET_TAX_RATE_SLABS)
            .then((res) => res?.data?.data || {}),
          api.get(END_POINT.RATE_PLAN.GET_ALL).then((res) => res?.data?.data)
            .catch(() => { }),
          api.get(END_POINT.UTILS.GET_ALL_STAY_TYPES).then((res) => res?.data?.data).catch(() => []),
        ]);
      setRoomTypes(roomTypes);
      setReservationStatuses(reservationStatus);
      setRoomShiftReasons(roomShiftReasons);
      setTaxRates(consolidateTaxRatesSlabs(taxRates));
      setRatePlans(ratePlansRes || []);
      setStayTypes(stayTypesRes || []);

    } catch (error) { }
  };

  const getSelectedRoomType = (selectedRoomTypeId = getValues("room_type_id")) =>
    roomTypes?.find(
      (type) => Number(type?.room_type_id) === Number(selectedRoomTypeId)
    );

  const getOccupancyLimit = (fieldName, selectedRoomTypeId) => {
    const roomType = getSelectedRoomType(selectedRoomTypeId);

    if (fieldName === "no_of_adults") {
      return Number(roomType?.max_adult);
    }

    if (fieldName === "no_of_children") {
      return Number(roomType?.max_children);
    }

    return NaN;
  };

  const limitOccupancyValue = (fieldName, fieldValue, selectedRoomTypeId) => {
    if (!["no_of_adults", "no_of_children"].includes(fieldName)) {
      return fieldValue;
    }

    if (fieldValue === "" || fieldValue === null || fieldValue === undefined) {
      return "";
    }

    const maxValue = getOccupancyLimit(fieldName, selectedRoomTypeId);
    const minValue = fieldName === "no_of_adults" ? 1 : 0;
    const numericValue = Number(fieldValue);

    if (Number.isNaN(numericValue)) {
      return "";
    }

    if (Number.isFinite(maxValue) && numericValue > maxValue) {
      return String(maxValue);
    }

    if (numericValue < minValue) {
      return fieldName === "no_of_adults" ? "" : String(minValue);
    }

    return String(numericValue);
  };

  const getOccupancyError = (fieldName, fieldValue, selectedRoomTypeId) => {
    const numericValue = Number(fieldValue);
    const roomType = getSelectedRoomType(selectedRoomTypeId);

    if (fieldValue === "" || Number.isNaN(numericValue)) {
      return null;
    }

    if (fieldName === "no_of_adults" && numericValue < 1) {
      return "Minimum of 1 adult should be selected";
    }

    if (!roomType) {
      return null;
    }

    if (
      fieldName === "no_of_adults" &&
      Number.isFinite(Number(roomType.max_adult)) &&
      numericValue > Number(roomType.max_adult)
    ) {
      return t("pages.booking_form.alert.only_adults_are_permitted", {
        count: roomType.max_adult,
      });
    }

    if (
      fieldName === "no_of_children" &&
      Number.isFinite(Number(roomType.max_children)) &&
      numericValue > Number(roomType.max_children)
    ) {
      return t("pages.booking_form.alert.only_childs_are_permitted", {
        count: roomType.max_children,
      });
    }

    return null;
  };

  const setOccupancyFieldError = (fieldName, message) => {
    if (message) {
      setError(fieldName, {
        type: "manual",
        message,
      });
      return false;
    }

    clearErrors(fieldName);
    return true;
  };

  const handleChange = (e, options) => {
    const { name, value, type } = e.target;
    const limitedValue = limitOccupancyValue(name, value);

    if (type != "number" || name === "room_rent") {
      clearErrors(name);
    }

    if (limitedValue !== value) {
      e.target.value = limitedValue;
    }

    setValue(name, limitedValue, { shouldValidate: true, shouldDirty: true });

    if (["no_of_adults", "no_of_children"].includes(name)) {
      const occupancyError = getOccupancyError(name, limitedValue);
      const isValidOccupancy = setOccupancyFieldError(name, occupancyError);

      if (!isValidOccupancy) {
        return;
      }
    }

    // Smart Pricing & Restore Logic
    if (
      ["room_type_id", "rate_plan_id", "no_of_adults", "no_of_children"].includes(
        name
      )
    ) {
      const currentValues = getValues();
      const isHandled = handleSmartPricingLogic({
        ...currentValues,
        [name]: limitedValue,
      });
      // We don't return early here if it's room_type_id because we need to run the reset logic below
      if (isHandled && name !== "room_type_id") return;
    }

    if (name === "room_type_id") {
      setValue("rate_plan_id", "");
      const { room_base_price, room_type_name } =
        roomTypes?.find((type) => Number(type?.room_type_id) === Number(value)) || {};
      const limitedAdults = limitOccupancyValue("no_of_adults", no_of_adults, value);
      const limitedChildren = limitOccupancyValue("no_of_children", no_of_children, value);
      const commonData = {
        room_type_id: value,
        rate_plan_id: "",
        inc_of_tax: inc_of_tax,
        room_id: null,
        no_of_children: limitedChildren,
        no_of_adults: limitedAdults,
        room_type_name,
        room_shift_reason_id,
      };

      if (value === bookingData?.room_type_id) {
        reset(
          {
            ...commonData,
            room_rent: inc_of_tax
              ? getNetRetFromBasePrice(
                bookingData.room_rent,
                1,
                bookingData.total_tax_rate
              )
              : bookingData.room_rent,
          },
          { keepErrors: true }
        );
      } else {
        reset(
          {
            ...commonData,
            room_rent: room_base_price,
          },
          { keepErrors: true }
        );
      }

      setOccupancyFieldError(
        "no_of_adults",
        getOccupancyError("no_of_adults", limitedAdults, value)
      );
      setOccupancyFieldError(
        "no_of_children",
        getOccupancyError("no_of_children", limitedChildren, value)
      );

      setAvailableRoomsByType([]);
    } else if (name === "room_id") {
      const selectedOption = options?.find((option) => option?.value === value);
      setValue("room_no", selectedOption?.label);
      if (WARN_ON_CLEANING_STATUSES.includes(selectedOption?.cleaning_status_code)) {
        setDirtyRoomDialog({
          open: true,
          roomNo: selectedOption?.label,
          statusLabel: CLEANING_STATUS_LABELS[selectedOption.cleaning_status_code],
        });
      }
    } else if (name === "no_of_adults") {
      setOccupancyFieldError(
        "no_of_adults",
        getOccupancyError("no_of_adults", limitedValue)
      );
    } else if (name === "no_of_children") {
      setOccupancyFieldError(
        "no_of_children",
        getOccupancyError("no_of_children", limitedValue)
      );
    }
  };

  useEffect(() => {
    if (!!room_type_id) {
      setAvailableRoomsData(room_type_id);
    }
  }, [room_type_id]);

  const setAvailableRoomsData = async (roomTypeId) => {
    try {
      const res = await api.post(
        isOccupancyRoomShift
          ? END_POINT.ROOM.GET_AVAILABLE_ROOMS
          : END_POINT.ROOM.GET_AVAILABLE_ROOMS_TO_RESERVE,
        {
          checkin_date: getLocalTimeFromUtc(checkin_date)?.dateTimeString,
          checkout_date: getLocalTimeFromUtc(checkout_date)?.dateTimeString,
          room_type_id: roomTypeId,
        }
      );
      if (res?.status === 200) {
        const availableRooms = res?.data?.data.filter(
          (room) => room.room_id !== bookingData?.room_id
        );
        setAvailableRoomsByType(availableRooms);
      }
    } catch (error) {
      setAvailableRoomsByType([]);
    }
  };

  const handleInput = (e) => {
    const { value, name } = e.target;
    let sanitizedValue = value.replace(/[^0-9]/g, "");
    // Prevent multiple leading zeros
    if (sanitizedValue.length > 1 && sanitizedValue.startsWith("0")) {
      sanitizedValue = sanitizedValue.replace(/^0+/, "");
    }
    const normalizedValue =
      name !== "no_of_children" && sanitizedValue === "0"
        ? ""
        : sanitizedValue;

    e.target.value = limitOccupancyValue(name, normalizedValue);
  };


  //  const debouncedhandleRoomShift = useCallback(
  //   debounce((data, taxPercent) => {
  //     handleRoomShift({ ...data, taxPercent });
  //   }, 500),
  //   []
  // );

  const handleRoomShift = async (formData) => {
    setIsSubmitted(true);
    const {
      room_type_id,
      room_id,
      room_shift_reason_id,
      no_of_adults,
      no_of_children,
      room_rent,
      taxPercent,
      rate_plan_id,
    } = formData ?? {};

    let totalCalculatedPrice = 0;
    const stayTypeObj = stayTypes.find((st) => st.stay_type_id === stay_type_id || st.stay_type_code === stayTypeCode);
    const stayCalc = getStayCalculation(
      localCheckin,
      localCheckout,
      stayTypeCode,
      stayTypeObj?.standard_checkin_time,
      stayTypeObj?.standard_checkout_time
    );
    const formattedDayRents = (getValues("day_rents") || []).map((rent) => {
      const taxObj = getTaxPercentForAmount(rent.amount, taxRates, bookingData?.inc_of_tax);
      const rowTaxRate = taxObj.rate;

      const calculatedAmount = getBasePriceFromFomNetRent(
        Number(rent.amount),
        bookingData?.inc_of_tax,
        rowTaxRate
      );

      totalCalculatedPrice += calculatedAmount;

      const graceHours = rent.grace_hours || extractGraceHoursFromLabel(rent.date) || null;

      let resolvedDate = normalizeRoomShiftDate(rent.date);
      if (graceHours) {
        if (stayCalc?.lateCheckoutGrace && rent.date === stayCalc.lateCheckoutGrace) {
          resolvedDate = localCheckout;            // late checkout grace
        } else if (stayCalc?.earlyCheckinGrace && rent.date === stayCalc.earlyCheckinGrace) {
          resolvedDate = localCheckin;             // early check-in grace
        } else {
          // fallback: match by calendar date
          const dOut = format(new Date(localCheckout), "yyyy-MM-dd");
          resolvedDate = resolvedDate === dOut ? localCheckout : localCheckin;
        }
      }

      const result = {
        date: resolvedDate,
        amount: calculatedAmount,
        rate_type: rent.rate_type,
        tax_rate: rowTaxRate,
        grace_hours: graceHours,
        ...getTaxBreakdown(taxObj),
      };

      return result;
    });

    const booking_price = Number(totalCalculatedPrice.toFixed(2));

    // TODO: RATE_PLAN_CHANGE - Fetch price for new Room + Rate Plan combo
    try {
      let response;
      const payload = {
        room_type_id,
        room_id,
        booking_price,
        current_date: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
        room_shift_reason_id,
        no_of_adults,
        no_of_children,
        rate_plan_id,
        day_rents: formattedDayRents
      };
      console.log("DEBUG: ROOM SHIFT PAYLOAD SENDING TO BACKEND:", payload);

      if (isOccupancyRoomShift) {
        response = await api.post(
          `${END_POINT.ROOM.ROOM_SHIFT}/${occupancy_key}`,
          payload
        );
      } else {
        response = await api.post(END_POINT.ROOM.RESERVATION_ROOM_SHIFT, {
          reserved_room_type_id: room_type_id,
          reserved_room_id: room_id,
          reservation_id,
          stay_type_id,
          reservation_price: booking_price,
          expected_no_of_adults: no_of_adults,
          expected_no_of_children: no_of_children,
          total_tax_rate: taxPercent,
          rate_plan_id,
          day_rents: formattedDayRents,
        });
      }

      if (response?.status === 200) {
        handleClosePopover();
        successToster(
          <Trans
            i18nKey="common_components.room_shift_popup.toast.room_shifted_message"
            values={{
              name,
              oldRoomNo: bookingData?.room_no,
              newRoomNo: getValues("room_no"),
            }}
            components={{ strong: <strong /> }}
          />
        );
        dispatch(setTriggered(!isTriggered));
        resetData();
        const roomTypes = await api
          .get(
            `${END_POINT.ROOM.GET_ALL_ROOMS_BY_ROOMTYPE}/${format(
              new Date(),
              "yyyy-MM-dd"
            )}`
          )
          .then((res) => res?.data?.data || [])
          .catch((error) => {
            return [];
          });

        const vacantRooms = await fetchAndFilterVacantRooms(roomTypes);
        await setDataToDB("roomTypes", vacantRooms);
      }
    } catch (error) {
      errorToster(
        t(
          `common_components.room_shift_popup.alert.${error?.response?.data?.msg
            ?.toLowerCase()
            ?.replace(/ /g, "_")}`
        ) || "Something went wrong!"
      );
    } finally {
      setTimeout(() => {
        setIsSubmitted(false);
      }, 2000);
    }
  };

  const handleProceedDirtyRoom = () => {
    setDirtyRoomDialog({ open: false, roomNo: null, statusLabel: null });
  };

  const handleGoWithAnotherRoom = () => {
    setValue("room_id", null);
    setValue("room_no", null);
    setDirtyRoomDialog({ open: false, roomNo: null, statusLabel: null });
  };

  const selectedRoomType = getSelectedRoomType(room_type_id);

  const fields = [
    {
      label: "Room Type",
      name: "room_type_id",
      type: "select",
      size: "small",
      value: room_type_id,
      width: { xs: "48%", sm: "48%" },
      options: roomTypes?.map((val, ind) => ({
        value: val.room_type_id,
        label: val.room_type_name,
      })),
      noOptionsLabel: "No Room Types Available",
    },
    {
      label: "Room Number",
      name: "room_id",
      type: "select",
      size: "small",
      value: room_id,
      width: { xs: "48%", sm: "48%" },
      options: availableRoomsByType?.map((room, ind) => ({
        value: room.room_id,
        label: room.room_no,
        status: room.reservation_status_code,
        cleaning_status_code: room.cleaning_status_code,
      })),
      noOptionsLabel: "No Rooms Available",
      labelStyle: { fontSize: "1px" },
      MenuProps: {
        PaperProps: {
          style: {
            maxHeight: "1vh", // Set dropdown menu height
            overflowY: "auto", // Enable scrolling if content overflows
          },
        },
      },
    },
    {
      label: t("pages.booking_form.rate_plan"),
      name: "rate_plan_id",
      type: "select",
      size: "small",
      width: { xs: "100%", sm: "100%" },
      marginTop: "4.5%",
      disable: !room_type_id,
      options: filterRatePlansForRoomType(
        ratePlans,
        room_type_id,
        rate_plan_id,
        isCmBooking
      ).map((val) => ({
        value: val.rate_plan_id,
        label: val.rate_plan_name,
      })),
      noOptionsLabel: room_type_id ? "No Rate Plans for this type" : "Select Room Type First",
    },
    {
      label: "Number of Adults",
      name: "no_of_adults",
      type: "number",
      size: "small",
      width: { xs: "48%", sm: "48%" },
      min: 1,
      max: selectedRoomType?.max_adult,
    },
    {
      label: "Number of Children",
      name: "no_of_children",
      type: "number",
      size: "small",
      width: { xs: "48%", sm: "48%" },
      min: 0,
      max: selectedRoomType?.max_children,
    },
    {
      label: "Room Rent",
      name: "room_rent",
      type: "number",
      size: "small",
      width: { xs: "100%" },
      disable: roomRentDisable,
      inputAdornment: (
        <InputAdornment position="end">
          <IconButton
            size="small"
            onClick={() => setRoomRentDisable(!roomRentDisable)}
          >
            {roomRentDisable ? (
              <Edit fontSize="small" />
            ) : (
              <Cancel fontSize="small" />
            )}
          </IconButton>
        </InputAdornment>
      ),
    },
    {
      label: "Inclusive Of Tax",
      type: "checkbox",
      disable: true,
      name: "inc_of_tax",
      width: { xs: "100%" },
    },
    {
      label: "Select Room Shift Reason",
      name: "room_shift_reason_id",
      type: "select",
      size: "small",
      value: room_shift_reason_id,
      width: { xs: "100%" },
      options: roomShiftReasons?.map((reason, ind) => ({
        value: reason.room_shift_reason_id,
        label: t(
          `common_components.room_shift_popup.reasons.${reason.reason
            .toLowerCase()
            .replace(/\s+/g, "_")}`
        ),
      })),
      noOptionsLabel: "No Reasons Available",
    },
  ];

  return (
    <Box
      sx={{
        width: {
          sm: "70vw",
          xs: "90vw",
          md: "41vw",
        },
        padding: { xs: "2% 0 3% 0", md: "0.5% 0 1.5% 0" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          margin: "0 auto 6% auto",
          width: "94%",
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: { xs: "3.846vw", sm: "2.398vw", md: "1.39vw" },
          }}
        >
          {t("common_components.room_shift_popup.room_shift")}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: "2.821vw", sm: "1.12vw", md: "1.12vw" },
            fontWeight: 500,
            ...getGradientTextStyle(palette),
          }}
        >
          {bookingData?.name}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "94%",
          margin: "0 auto",
        }}
      >
        <Box sx={{ width: "10%" }}>
          <Box
            sx={{
              background: "#F7F2E8",
              borderRadius: "50%",
              width: { xs: "7.692vw", sm: "5vw", md: "2.92vw" },
              height: {
                xs: "7.692vw",
                sm: "5vw",
                md: "2.92vw",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "3.08vw", sm: "1.44vw", md: "0.83vw" },
                color: "#252525",
                fontWeight: 600,
              }}
            >
              {bookingData?.room_no}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ width: "88%" }}>
          <form
            onSubmit={handleSubmit((data) =>
              // debouncedhandleRoomShift(data, bookingData.total_tax_rate)
              handleRoomShift({ ...data, taxPercent: bookingData.total_tax_rate })
            )}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                width: 1,
              }}
            >
              {fields
                .filter((field) =>
                  isOccupancyRoomShift
                    ? field
                    : field.name !== "room_shift_reason_id"
                )
                .map((roomField) => (
                  <Controller
                    {...methods}
                    key={roomField.name}
                    control={control}
                    name={roomField.name}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => {
                      if (roomField.name === "room_rent") {
                        return (
                          <FormControl sx={{ width: roomField?.width, marginTop: "20px", position: "relative" }}>
                            <TextField
                              {...field}
                              label={t(`common_components.room_shift_popup.${roomField?.label.toLowerCase().replace(/ /g, "_")}`)}
                              size="small"
                              disabled={true}
                              error={!!error}
                              helperText={error?.message}
                              InputProps={{
                                endAdornment: roomField?.inputAdornment,
                                sx: { fontSize: { xs: "4.1vw", sm: "1.12vw" } },
                              }}
                            />


                            <EditRent
                              isEditingRent={!roomRentDisable}
                              roomIndex={0} // RoomShift-la eppovume single room thaan, so 0
                              dayRents={watch("day_rents") || []}
                              setValue={setValue}
                              onSave={(updatedDayRents) => {
                                setValue("day_rents", updatedDayRents); // Updated manual values inga store aagum

                                // Total sum-ah recalculate panni display value-ah update panrom
                                const displayValue = getRoomRentDisplayValue(updatedDayRents);
                                setValue("room_rent", displayValue, { shouldValidate: true });

                                setRoomRentDisable(true); // Popup-ah close panna
                              }}
                              setRoomPriceDisable={() => setRoomRentDisable(true)} // Save pannuna popup close aaga
                              onToggle={() => setRoomRentDisable(true)}
                            />
                          </FormControl>
                        );
                      }
                      return roomField.type === "select" ? (
                        <FormControl sx={{ width: roomField?.width, marginTop: roomField.name === "rate_plan_id" ? "20px" : "15px" }}>
                          <TextField
                            {...field}
                            autoComplete="off"
                            select
                            name={roomField?.name}
                            size={roomField?.size}
                            label={t(
                              `common_components.room_shift_popup.${roomField?.label
                                .toLowerCase()
                                .replace(/ /g, "_")}`
                            )}
                            disabled={roomField.disable}
                            onChange={(e) =>
                              handleChange(e, roomField?.options)
                            }
                            InputLabelProps={{
                              sx: {
                                fontSize: {
                                  xs: "3.5vw",
                                  sm: "1.92vw",
                                  md: "1.12vw",
                                },
                                color: "#A3A3A3",
                              },
                            }}
                            InputProps={{
                              sx: {
                                fontSize: {
                                  xs: "4.1vw",
                                  sm: "1.92vw",
                                  md: "1.12vw",
                                },
                              },
                            }}
                            FormHelperTextProps={{
                              sx: {
                                fontSize: {
                                  xs: "3.08vw",
                                  sm: "1.44vw",
                                  md: "0.83vw",
                                },
                              },
                            }}
                            error={!!error?.message}
                            helperText={!!error?.message ? error?.message : ""}
                            SelectProps={{
                              MenuProps: {
                                PaperProps: {
                                  style: {
                                    maxHeight: "45vw",
                                    overflowY: "auto",
                                  },
                                },
                              },
                            }}
                          >
                            {roomField?.options?.length > 0 ? (
                              roomField?.options?.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                  disabled={
                                    roomField.name === "room_id" &&
                                    [TENTATIVE, CONFIRMED].includes(
                                      reservationStatuses.find(
                                        (status) =>
                                          status.reservation_status_code ===
                                          option.status
                                      )?.reservation_status_code
                                    )
                                  }
                                  sx={{
                                    backgroundColor:
                                      roomField.name === "room_id"
                                        ? [TENTATIVE, CONFIRMED].includes(
                                          reservationStatuses.find(
                                            (status) =>
                                              status.reservation_status_code ===
                                              option.status
                                          )?.reservation_status_code
                                        )
                                          ? "#9747FF"
                                          : "#D9FFD8"
                                        : "",
                                    fontSize: {
                                      xs: "3.5vw",
                                      sm: "1.92vw",
                                      md: "1.12vw",
                                    },
                                  }}
                                >
                                  {roomField.name === "room_id" &&
                                  CLEANING_STATUS_ICONS[option.cleaning_status_code] ? (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        columnGap: { xs: "6px", sm: "6px", md: "0.4vw" },
                                      }}
                                    >
                                      <Box
                                        component="img"
                                        src={CLEANING_STATUS_ICONS[option.cleaning_status_code]}
                                        alt={option.cleaning_status_code}
                                        sx={{
                                          width: { xs: "16px", sm: "16px", md: "1.1vw" },
                                          height: { xs: "16px", sm: "16px", md: "1.1vw" },
                                        }}
                                      />
                                      <span>{option.label}</span>
                                    </Box>
                                  ) : (
                                    option.label
                                  )}
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem value="" disabled>
                                {t(
                                  `common_components.room_shift_popup.${roomField.noOptionsLabel
                                    .toLowerCase()
                                    .replace(/\s+/g, "_")}`
                                )}
                                {roomField.noOptionsLabel}
                              </MenuItem>
                            )}
                          </TextField>
                        </FormControl>
                      ) : roomField.type === "checkbox" ? (
                        <FormControl sx={{ width: roomField?.width }}>
                          <FormControlLabel
                            {...field}
                            sx={{
                              marginLeft: "auto",
                              "& .MuiFormControlLabel-label": {
                                fontSize: {
                                  xs: "2.308vw",
                                  sm: "1.92vw",
                                  md: "1.1vw",
                                },
                              },
                            }}
                            value={inc_of_tax}
                            name={roomField.name}
                            disabled={roomField.disable}
                            control={
                              <Checkbox checked={field?.value} size="small" />
                            }
                            label={t(
                              `common_components.room_shift_popup.${roomField?.label
                                .toLowerCase()
                                .replace(/ /g, "_")}`
                            )}
                            labelPlacement="end"
                          />
                        </FormControl>
                      ) : (
                        <FormControl
                          sx={{ width: roomField?.width, marginTop: "6%" }}
                        >
                          <TextField
                            {...field}
                            name={roomField.name}
                            variant="outlined"
                            className="NoSpinnerTextField no-spinner"
                            onWheel={(e) => e.target.blur()}
                            value={getValues(roomField.name)}
                            type={roomField.type}
                            size={roomField?.size}
                            label={t(
                              `common_components.room_shift_popup.${roomField?.label
                                .toLowerCase()
                                .replace(/ /g, "_")}`
                            )}
                            inputProps={{
                              min: roomField.min ?? 0,
                              maxLength: !!roomField.maxLength
                                ? roomField.maxLength
                                : "",
                              max: roomField.max ?? "",
                            }}
                            InputProps={{
                              endAdornment: roomField?.inputAdornment,
                              sx: {
                                fontSize: {
                                  xs: "4.1vw",
                                  sm: "1.92vw",
                                  md: "1.12vw",
                                },
                                backgroundColor:
                                  roomField?.name === "total_price" &&
                                  "#EBEBEB",
                              },
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: {
                                  xs: "4.1vw",
                                  sm: "1.92vw",
                                  md: "1.12vw",
                                },
                                color: "#A3A3A3",
                              },
                            }}
                            FormHelperTextProps={{
                              sx: {
                                fontSize: {
                                  xs: "3.08vw",
                                  sm: "1.44vw",
                                  md: "0.83vw",
                                },
                              },
                            }}
                            onInput={(e) => handleInput(e)}
                            onChange={(e) => handleChange(e)}
                            disabled={roomField.disable}
                            error={!!error?.message}
                            helperText={!!error?.message ? error?.message : ""}
                          />
                        </FormControl>
                      );
                    }}
                  />
                ))}

              <Button
                onClick={handleCloseModal}
                variant="outlined"
                sx={{
                  width: "48%",
                  fontSize: { xs: "3.08vw", sm: "1.92vw", md: "1.12vw" },
                  fontWeight: 700,
                  marginTop: "5%",
                }}
              >
                {t("common_components.room_shift_popup.cancel")}
              </Button>
              <Can feature={FEATURES.RESERVATIONS} action={ACTIONS.ROOM_SHIFT}>
                <Button
                  variant="contained"
                  disabled={room_id === bookingData?.room_id || isSubmitted}
                  type="submit"
                  sx={{
                    width: "48%",
                    fontSize: { xs: "3.08vw", sm: "1.92vw", md: "1.12vw" },
                    fontWeight: 700,
                    marginTop: "5%",
                  }}
                >
                  {t("common_components.room_shift_popup.confirm")}
                </Button>
              </Can>
            </Box>
          </form>
        </Box>
      </Box>
      <ConfirmationDlgBox
        open={dirtyRoomDialog.open}
        label={`Room ${dirtyRoomDialog.roomNo} is ${dirtyRoomDialog.statusLabel}. Do you want to proceed?`}
        confirmFunction={handleProceedDirtyRoom}
        cancelFunction={handleGoWithAnotherRoom}
        setOpen={(open) =>
          setDirtyRoomDialog((prev) => ({ ...prev, open }))
        }
        confirmLabel="Proceed"
        cancelLabel="Cancel"
      />
    </Box>
  );
};

export default RoomShift;
